import React, { useEffect, useState, useMemo } from 'react';
import Select, { components } from 'react-select';
import { useTranslation } from 'react-i18next';
import { getCountries } from '../utils/countries';
import { MapPin } from 'lucide-react';

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
}

// Composant personnalisé pour le contrôle avec l'icône
const Control = ({ children, ...props }: any) => {
  const { isFocused, hasValue } = props;
  return (
    <components.Control {...props}>
      {!isFocused && !hasValue && (
        <MapPin className="w-5 h-5 text-gray-500 ml-3 mr-1" />
      )}
      {children}
    </components.Control>
  );
};

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  isDisabled = false
}) => {
  const { t, i18n } = useTranslation();
  
  // État local pour les pays
  const [countriesList, setCountries] = useState(() => getCountries(i18n.language));
  
  // Mettre à jour la liste des pays quand la langue change
  useEffect(() => {
    const newCountries = getCountries(i18n.language);
    setCountries(newCountries);
  }, [i18n.language]);
  
  // Trouver le pays sélectionné
  const selectedCountry = useMemo(() => 
    countriesList.find(country => country.value === value), 
    [countriesList, value]
  );

  // Style personnalisé pour le select
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#0A0A0A',
      borderColor: state.isFocused ? '#B8A44E' : '#1f2937',
      borderRadius: '0.75rem',
      minHeight: '46px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#B8A44E' : '#4b5563'
      },
      paddingLeft: '0'
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: '#0A0A0A',
      border: '1px solid #1f2937',
      borderRadius: '0.75rem',
      zIndex: 10
    }),
    option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'rgba(184, 164, 78, 0.2)' 
        : state.isFocused 
          ? 'rgba(184, 164, 78, 0.1)' 
          : '#0A0A0A',
      color: state.isSelected ? '#B8A44E' : 'white',
      '&:active': {
        backgroundColor: 'rgba(184, 164, 78, 0.2)'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'white',
      marginLeft: '0'
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'white',
      marginLeft: '0'
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6b7280',
      marginLeft: '0'
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: '#4b5563'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#6b7280',
      '&:hover': {
        color: '#9ca3af'
      }
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      color: '#6b7280',
      '&:hover': {
        color: '#9ca3af'
      }
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      paddingLeft: '12px'
    })
  };

  const handleChange = (option: any) => {
    if (option) {
      onChange(option.value);
    } else {
      onChange('');
    }
  };

  return (
    <Select
      value={selectedCountry}
      onChange={handleChange}
      options={countriesList}
      styles={customStyles}
      placeholder={placeholder || t('home.additionalInfo.country')}
      className={className}
      isDisabled={isDisabled}
      isClearable
      isSearchable
      classNamePrefix="country-select"
      components={{ Control }}
    />
  );
};

export default CountrySelect; 